import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import LoginHeader from '../login/components/LoginHeader';
import LoginBackground from '../login/components/LoginBackground';
import { supabase, isSupabaseConfigured } from '../../lib/supabase';

// ---------------------------------------------------------------------------
// Password reset — step 2 of 2.
//
// The user arrives here from the magic link in their email. Supabase's client
// (configured with detectSessionInUrl: true in lib/supabase.js) automatically
// exchanges the recovery token in the URL fragment for a real auth session
// and fires a PASSWORD_RECOVERY event. We listen for that event to know the
// user is genuinely mid-reset (not just poking around on /reset-password) and
// enable the new-password form.
//
// If someone hits /reset-password without a valid recovery session we show a
// friendly "your link is invalid or expired" screen instead of a broken form.
// ---------------------------------------------------------------------------

const MIN_PASSWORD_LENGTH = 8;

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const supabaseLive = isSupabaseConfigured() && supabase !== null;

  const [sessionReady, setSessionReady] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [recoveryFlow, setRecoveryFlow] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!supabaseLive) {
      setSessionChecked(true);
      return undefined;
    }

    let cancelled = false;

    // Ask Supabase whether we already have a session (detectSessionInUrl will
    // parse the recovery token from the URL fragment before we mount).
    const checkExisting = async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        if (data?.session) {
          setSessionReady(true);
        }
      } catch {
        // fall through — we'll rely on the auth-state listener below
      } finally {
        if (!cancelled) setSessionChecked(true);
      }
    };
    checkExisting();

    // Listen for PASSWORD_RECOVERY to confirm this is a real recovery flow.
    // Supabase fires this event once when the client processes a recovery
    // token from the URL. Some browsers race the initial render, so we treat
    // ANY signed-in session on this page as reset-eligible — but we still
    // prefer the explicit recovery event when it fires.
    const { data: subscription } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setRecoveryFlow(true);
        setSessionReady(Boolean(session));
      } else if (event === 'SIGNED_IN' && session) {
        setSessionReady(true);
      }
    });

    return () => {
      cancelled = true;
      subscription?.subscription?.unsubscribe?.();
    };
  }, [supabaseLive]);

  const passwordProblems = (() => {
    const problems = [];
    if (password.length < MIN_PASSWORD_LENGTH) {
      problems.push(`Must be at least ${MIN_PASSWORD_LENGTH} characters`);
    }
    if (!/[A-Za-z]/.test(password)) {
      problems.push('Must contain at least one letter');
    }
    if (!/[0-9]/.test(password)) {
      problems.push('Must contain at least one number');
    }
    if (password && confirmPassword && password !== confirmPassword) {
      problems.push('The two passwords must match');
    }
    return problems;
  })();

  const canSubmit = password && confirmPassword && passwordProblems.length === 0;

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    if (!supabaseLive) {
      setError(
        'Password reset requires a connected Supabase project. This app is currently in demo mode.',
      );
      return;
    }

    if (passwordProblems.length > 0) {
      setError(passwordProblems[0]);
      return;
    }

    setSubmitting(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) {
        setError(updateError.message || 'Could not update password. Please try again.');
        return;
      }
      setSuccess(true);
      // Give the user a moment to read the success banner, then send them home.
      setTimeout(() => navigate('/dashboard'), 1500);
    } catch (err) {
      setError(err?.message || 'Could not update password. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render branches
  // -------------------------------------------------------------------------

  const invalidLink =
    supabaseLive && sessionChecked && !sessionReady && !recoveryFlow && !success;

  return (
    <div className="min-h-screen bg-background flex">
      <div className="flex-1 lg:w-1/2 flex items-center justify-center p-6 lg:p-12">
        <div className="w-full max-w-md">
          <LoginHeader />

          {success && (
            <div className="p-4 bg-success/10 border border-success/30 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <Icon
                  name="CheckCircle2"
                  size={20}
                  className="text-success flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="font-medium text-foreground">Password updated</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    You're signed in. Redirecting to your dashboard…
                  </p>
                </div>
              </div>
            </div>
          )}

          {!supabaseLive && !success && (
            <div className="p-4 bg-warning/10 border border-warning/30 rounded-lg mb-4">
              <div className="flex items-start gap-2">
                <Icon
                  name="Info"
                  size={16}
                  className="text-warning flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="text-sm font-medium text-foreground">
                    Password reset unavailable
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    This app is running in <strong>Demo mode</strong>, so there's no cloud
                    account to reset. Ask your admin to connect Supabase, then try again.
                  </p>
                </div>
              </div>
              <div className="mt-4">
                <Link
                  to="/login"
                  className="text-sm text-primary hover:text-primary/80 inline-flex items-center gap-1"
                >
                  <Icon name="ArrowLeft" size={14} />
                  Back to sign in
                </Link>
              </div>
            </div>
          )}

          {invalidLink && (
            <div className="p-4 bg-warning/10 border border-warning/30 rounded-lg mb-4">
              <div className="flex items-start gap-3">
                <Icon
                  name="AlertTriangle"
                  size={20}
                  className="text-warning flex-shrink-0 mt-0.5"
                />
                <div>
                  <p className="font-medium text-foreground">Reset link expired or invalid</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Password-reset links can only be used once, and they expire about an
                    hour after being sent. Request a fresh link and try again.
                  </p>
                </div>
              </div>
              <div className="mt-4 flex gap-2 flex-wrap">
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => navigate('/forgot-password')}
                >
                  <Icon name="Mail" size={14} />
                  Send a new reset link
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/login')}
                >
                  <Icon name="ArrowLeft" size={14} />
                  Back to sign in
                </Button>
              </div>
            </div>
          )}

          {supabaseLive && sessionReady && !success && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Choose a new password
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Your recovery link worked. Pick a new password below — you'll be signed
                  in as soon as you save it.
                </p>
              </div>

              {error && (
                <div className="p-3 rounded-md bg-destructive/10 border border-destructive/30">
                  <div className="flex items-start gap-2">
                    <Icon
                      name="AlertCircle"
                      size={16}
                      className="text-destructive flex-shrink-0 mt-0.5"
                    />
                    <p className="text-sm text-foreground">{error}</p>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Input
                  label="New password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="At least 8 characters, includes a letter and a number"
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value ?? '')}
                  required
                  autoComplete="new-password"
                />
                <Input
                  label="Confirm new password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Type it again"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e?.target?.value ?? '')}
                  required
                  autoComplete="new-password"
                />
                <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={showPassword}
                    onChange={(e) => setShowPassword(e.target.checked)}
                    className="h-3.5 w-3.5"
                  />
                  Show passwords
                </label>
              </div>

              {password && passwordProblems.length > 0 && (
                <ul className="text-xs text-muted-foreground space-y-1">
                  {passwordProblems.map((problem) => (
                    <li key={problem} className="flex items-center gap-1.5">
                      <Icon
                        name="X"
                        size={12}
                        className="text-destructive flex-shrink-0"
                      />
                      <span>{problem}</span>
                    </li>
                  ))}
                </ul>
              )}

              <Button
                type="submit"
                variant="default"
                loading={submitting}
                disabled={!canSubmit || submitting}
                fullWidth
                iconName={submitting ? 'Loader2' : 'Save'}
                iconPosition="left"
              >
                {submitting ? 'Updating…' : 'Set new password'}
              </Button>

              <div className="text-center">
                <Link
                  to="/login"
                  className="text-sm text-primary hover:text-primary/80 inline-flex items-center gap-1"
                >
                  <Icon name="ArrowLeft" size={14} />
                  Cancel and go back
                </Link>
              </div>
            </form>
          )}

          {supabaseLive && !sessionChecked && (
            <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
              <Icon name="Loader2" size={16} className="animate-spin mr-2" />
              Checking your reset link…
            </div>
          )}
        </div>
      </div>

      <LoginBackground />
    </div>
  );
};

export default ResetPasswordPage;
